import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  SuppressionScopes,
  SuppressionTypes,
  formatDateTime,
  messages,
  type Suppression,
} from '@nesc/shared';
import { api, errorMessage, type ListResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { useUrlFilters } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';
import { ResponsiveList, type Column } from '@/components/common/responsive-list';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfirmDialog, EmptyState } from '@/components/common/feedback';
import { FilterChips, PageHeader } from '@/components/common/page';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const SOURCE_LABELS: Record<string, string> = {
  manual: '手動',
  reply_detected: '返信検知',
  bounce: 'バウンス',
  import: '取込',
};

/** S73 除外リスト */
export const SettingsSuppressionsPage = () => {
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const { filters, setFilter } = useUrlFilters({ type: '', q: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Suppression | null>(null);

  const query = { type: filters.type || undefined, q: filters.q || undefined, limit: 50 };

  const { data, isLoading } = useQuery({
    queryKey: qk.suppressions(query),
    queryFn: () => api.get<ListResponse<Suppression>>('/suppressions', query),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/suppressions/${id}`),
    onSuccess: () => {
      toast.success('除外を解除しました');
      void queryClient.invalidateQueries({ queryKey: ['suppressions'] });
      setRemoveTarget(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const columns: Column<Suppression>[] = [
    {
      key: 'value',
      header: '対象',
      render: (row) => (
        <span className="break-all font-semibold">{row.value ?? row.companyName ?? '—'}</span>
      ),
    },
    {
      key: 'type',
      header: '種別',
      width: '160px',
      render: (row) => <StatusBadge kind="suppression_type" value={row.type} />,
    },
    {
      key: 'scope',
      header: '適用単位',
      width: '120px',
      render: (row) => SuppressionScopes.label(row.scope),
    },
    { key: 'reason', header: '理由', render: (row) => row.reason ?? '—' },
    {
      key: 'source',
      header: '出所',
      width: '100px',
      render: (row) => SOURCE_LABELS[row.source] ?? row.source,
    },
    {
      key: 'createdAt',
      header: '登録日時',
      width: '140px',
      render: (row) => <span className="tabular">{formatDateTime(row.createdAt)}</span>,
    },
  ];

  return (
    <>
      <PageHeader
        back="/settings"
        title={messages.settings.suppressions}
        badge={
          data?.meta.totalCount != null ? (
            <Badge variant="muted" aria-label={`該当 ${data.meta.totalCount} 件`}>
              {(filters.type || filters.q) ? '該当' : '全'} {data.meta.totalCount.toLocaleString()} 件
            </Badge>
          ) : undefined
        }
        actions={
          hasRole('manager') && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden />
              {messages.suppression.add}
            </Button>
          )
        }
      />

      <FilterChips className="mb-3">
        <Select
          value={filters.type as string}
          onChange={(e) => setFilter('type', e.target.value)}
          aria-label="種別で絞り込む"
          className="h-8 w-auto shrink-0 text-note lg:h-8"
        >
          <option value="">種別：すべて</option>
          {SuppressionTypes.list.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>
        <Input
          type="search"
          value={filters.q as string}
          onChange={(e) => setFilter('q', e.target.value)}
          placeholder="ドメイン・メールで検索"
          aria-label="除外を検索"
          className="h-8 w-56 shrink-0 text-note lg:h-8"
        />
      </FilterChips>

      <ResponsiveList
        columns={columns}
        rows={data?.data ?? []}
        isLoading={isLoading}
        rowActions={
          hasRole('manager')
            ? (row) => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setRemoveTarget(row)}
                  aria-label={`${row.value ?? row.companyName} の除外を解除する`}
                >
                  解除
                </Button>
              )
            : undefined
        }
        empty={<EmptyState title="除外の設定はありません" />}
        card={(row) => (
          <div className="space-y-1.5">
            <p className="flex flex-wrap items-center gap-2">
              <span className="min-w-0 break-all text-body font-semibold">
                {row.value ?? row.companyName ?? '—'}
              </span>
              <StatusBadge kind="suppression_type" value={row.type} />
            </p>
            <p className="text-note text-foreground-muted">
              {SuppressionScopes.label(row.scope)}・{SOURCE_LABELS[row.source] ?? row.source}
              {row.reason && `・${row.reason}`}
            </p>
            <p className="tabular text-note text-foreground-subtle">{formatDateTime(row.createdAt)}</p>
            {hasRole('manager') && (
              <Button variant="outline" size="sm" onClick={() => setRemoveTarget(row)}>
                {messages.suppression.remove}
              </Button>
            )}
          </div>
        )}
      />

      <CreateSuppressionDialog open={createOpen} onOpenChange={setCreateOpen} />

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title={messages.suppression.remove}
        summary={`「${removeTarget?.value ?? removeTarget?.companyName}」の除外を解除します。`}
        detail={
          <div className="space-y-2">
            <p className="text-body font-semibold">{messages.suppression.removeConfirm}</p>
            {removeTarget?.source === 'reply_detected' && !hasRole('admin') && (
              <p className="rounded-control border-2 border-foreground bg-muted p-3 text-body font-semibold">
                {messages.suppression.removeAdminOnly}
              </p>
            )}
          </div>
        }
        confirmText={messages.suppression.remove}
        onConfirm={() => removeTarget && remove.mutate(removeTarget.id)}
        isPending={remove.isPending}
      />
    </>
  );
};

const CreateSuppressionDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const [type, setType] = useState('do_not_contact');
  const [scope, setScope] = useState('domain');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => api.post('/suppressions', { type, scope, value, reason: reason || undefined }),
    onSuccess: () => {
      toast.success('除外に登録しました');
      void queryClient.invalidateQueries({ queryKey: ['suppressions'] });
      onOpenChange(false);
      setValue('');
      setReason('');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.suppression.add}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="種別" required>
            {(props) => (
              <Select {...props} value={type} onChange={(e) => setType(e.target.value)}>
                {SuppressionTypes.list.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}（{item.description}）
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="適用単位" required>
            {(props) => (
              <Select {...props} value={scope} onChange={(e) => setScope(e.target.value)}>
                {SuppressionScopes.list
                  .filter((item) => item.value !== 'company')
                  .map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
          <Field label="値" required>
            {(props) => (
              <Input
                {...props}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={scope === 'domain' ? 'example.co.jp' : scope === 'email' ? 'info@example.co.jp' : '03-1234-5678'}
              />
            )}
          </Field>
          <Field label={messages.common.reason}>
            {(props) => <Input {...props} value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button size="lg" onClick={() => mutation.mutate()} disabled={mutation.isPending || !value.trim()}>
            {messages.common.add}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
